// frontend/src/components/modal/SupplierModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Building2, User, MapPin, Tag, DollarSign,
  Zap, FileText, Plus, ChevronDown, ChevronUp, Star
} from 'lucide-react';
import SupplierCategoryModal from './SupplierCategoryModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BILLING_FREQ = ['monthly', 'quarterly', 'annual'];
const PAYMENT_METHODS = ['wire', 'check', 'card', 'cash', 'ACH', 'other'];
const CURRENCIES = ['USD', 'EUR', 'CAD', 'GBP', 'MXN'];

// Sections collapsibles
const Section = ({ title, icon: Icon, color, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-900/60 hover:bg-gray-900/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={15} className={`text-${color}-400`} />
          <span className="text-white text-sm font-medium">{title}</span>
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && <div className="p-4 bg-gray-800/30 space-y-3">{children}</div>}
    </div>
  );
};

// Champ texte simple
const Field = ({ label, name, value, onChange, type = 'text', placeholder = '', required = false }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
    />
  </div>
);

// Select
const SelectField = ({ label, name, value, onChange, children, required = false }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
    >
      {children}
    </select>
  </div>
);

// ── Composant principal ───────────────────────────────────────────────────────
const SupplierModal = ({ supplier, onClose }) => {
  const isEdit = !!supplier;

  const emptyForm = {
    company_name: '', trade_name: '', registration_number: '', vat_number: '', website: '',
    contact_first_name: '', contact_last_name: '', contact_title: '',
    contact_email: '', contact_phone: '', contact_mobile: '',
    address_line1: '', address_line2: '', city: '', zip_code: '',
    state_region: '', country: 'United States',
    category_id: '', status: 'active', is_preferred: false,
    currency: 'USD', payment_terms_days: 30, payment_method: '',
    account_number: '', contract_reference: '',
    subscription_reference: '', fixed_monthly_amount: '',
    contract_start_date: '', contract_renewal_date: '', billing_frequency: '',
    notes: '',
  };

  const [form,           setForm]           = useState(emptyForm);
  const [categories,     setCategories]     = useState([]);
  const [groups,         setGroups]         = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [catModalOpen,   setCatModalOpen]   = useState(false);
  const [showFixedFields, setShowFixedFields] = useState(false);

  // Charger catégories + groupes
  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, grpRes] = await Promise.all([
          axios.get(`${API_BASE}/suppliers/categories`),
          axios.get(`${API_BASE}/suppliers/categories/groups`),
        ]);
        setCategories(catRes.data || []);
        setGroups(grpRes.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  // Pré-remplir en mode édition
  useEffect(() => {
    if (supplier) {
      const f = { ...emptyForm, ...supplier };
      // Formatter les dates
      if (f.contract_start_date)   f.contract_start_date   = f.contract_start_date.split('T')[0];
      if (f.contract_renewal_date) f.contract_renewal_date = f.contract_renewal_date.split('T')[0];
      setForm(f);
      // Ouvrir les charges fixes si pertinent
      const cat = categories.find(c => c.id === supplier.category_id);
      if (cat?.group_name?.includes('Charges Fixes')) setShowFixedFields(true);
    }
  }, [supplier]);

  // Détecter si la catégorie sélectionnée est "Charges Fixes"
  useEffect(() => {
    if (!form.category_id) { setShowFixedFields(false); return; }
    const cat = categories.find(c => c.id === form.category_id);
    setShowFixedFields(cat?.group_name?.includes('Charges Fixes') || false);
  }, [form.category_id, categories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = { ...form };
    if (!payload.category_id)          delete payload.category_id;
    if (!payload.fixed_monthly_amount) delete payload.fixed_monthly_amount;
    if (!payload.contract_start_date)  delete payload.contract_start_date;
    if (!payload.contract_renewal_date)delete payload.contract_renewal_date;
    if (!payload.billing_frequency)    delete payload.billing_frequency;
    payload.payment_terms_days = parseInt(payload.payment_terms_days) || 30;

    try {
      if (isEdit) {
        await axios.put(`${API_BASE}/suppliers/${supplier.id}`, payload);
      } else {
        await axios.post(`${API_BASE}/suppliers/`, payload);
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryAdded = async () => {
    const catRes = await axios.get(`${API_BASE}/suppliers/categories`);
    setCategories(catRes.data || []);
  };

  // Grouper les catégories pour le select
  const grouped = categories.reduce((acc, cat) => {
    const g = cat.group_name || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(cat);
    return acc;
  }, {});

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">

          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-xl">
                <Building2 size={18} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="text-white font-semibold">
                  {isEdit ? 'Edit Supplier' : 'New Supplier'}
                </h2>
                <p className="text-gray-400 text-xs mt-0.5">
                  {isEdit ? supplier.company_name : 'Add a new vendor or service provider'}
                </p>
              </div>
            </div>
            <button onClick={() => onClose(false)} className="text-gray-400 hover:text-white p-1 rounded-lg transition-colors">
              <X size={20} />
            </button>
          </div>

          {/* Body — scrollable */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* ── Identification ── */}
            <Section title="Identification" icon={Building2} color="emerald" defaultOpen>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Company Name"       name="company_name"        value={form.company_name}        onChange={handleChange} required />
                <Field label="Trade Name"          name="trade_name"          value={form.trade_name}          onChange={handleChange} />
                <Field label="Registration / SIRET" name="registration_number" value={form.registration_number} onChange={handleChange} />
                <Field label="VAT Number"          name="vat_number"          value={form.vat_number}          onChange={handleChange} />
                <Field label="Website"             name="website"             value={form.website}             onChange={handleChange} type="url" placeholder="https://" />

                {/* Status + Preferred */}
                <div className="flex items-end gap-3">
                  <SelectField label="Status" name="status" value={form.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </SelectField>
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      name="is_preferred"
                      checked={form.is_preferred}
                      onChange={handleChange}
                      className="w-4 h-4 accent-orange-400"
                    />
                    <Star size={13} className="text-orange-400 fill-orange-400" />
                    <span className="text-xs text-gray-400">Preferred</span>
                  </label>
                </div>
              </div>
            </Section>

            {/* ── Contact ── */}
            <Section title="Primary Contact" icon={User} color="blue">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="First Name"  name="contact_first_name" value={form.contact_first_name} onChange={handleChange} />
                <Field label="Last Name"   name="contact_last_name"  value={form.contact_last_name}  onChange={handleChange} />
                <Field label="Title / Role" name="contact_title"     value={form.contact_title}      onChange={handleChange} />
                <Field label="Email"       name="contact_email"      value={form.contact_email}      onChange={handleChange} type="email" />
                <Field label="Phone"       name="contact_phone"      value={form.contact_phone}      onChange={handleChange} />
                <Field label="Mobile"      name="contact_mobile"     value={form.contact_mobile}     onChange={handleChange} />
              </div>
            </Section>

            {/* ── Address ── */}
            <Section title="Address" icon={MapPin} color="purple" defaultOpen={false}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Field label="Address Line 1" name="address_line1" value={form.address_line1} onChange={handleChange} />
                </div>
                <div className="sm:col-span-2">
                  <Field label="Address Line 2" name="address_line2" value={form.address_line2} onChange={handleChange} />
                </div>
                <Field label="City"          name="city"         value={form.city}         onChange={handleChange} />
                <Field label="ZIP Code"      name="zip_code"     value={form.zip_code}     onChange={handleChange} />
                <Field label="State / Region" name="state_region" value={form.state_region} onChange={handleChange} />
                <Field label="Country"       name="country"      value={form.country}      onChange={handleChange} />
              </div>
            </Section>

            {/* ── Category ── */}
            <Section title="Classification" icon={Tag} color="yellow">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Category</label>
                  <div className="flex gap-2">
                    <select
                      name="category_id"
                      value={form.category_id || ''}
                      onChange={handleChange}
                      className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">— Select —</option>
                      {Object.entries(grouped).map(([group, cats]) => (
                        <optgroup key={group} label={group}>
                          {cats.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setCatModalOpen(true)}
                      className="px-3 py-2 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-600/30 text-emerald-400 rounded-lg text-xs flex items-center gap-1 transition-colors"
                      title="Add new category"
                    >
                      <Plus size={13} /> New
                    </button>
                  </div>
                </div>
              </div>
            </Section>

            {/* ── Commercial ── */}
            <Section title="Commercial Terms" icon={DollarSign} color="green" defaultOpen={false}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField label="Currency" name="currency" value={form.currency} onChange={handleChange}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </SelectField>
                <Field label="Payment Terms (days)" name="payment_terms_days" value={form.payment_terms_days} onChange={handleChange} type="number" />
                <SelectField label="Payment Method" name="payment_method" value={form.payment_method} onChange={handleChange}>
                  <option value="">— Select —</option>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </SelectField>
                <Field label="Account Number (internal)" name="account_number"    value={form.account_number}    onChange={handleChange} />
                <Field label="Contract / Framework Ref"  name="contract_reference" value={form.contract_reference} onChange={handleChange} />
              </div>
            </Section>

            {/* ── Fixed Charges (conditionnel) ── */}
            {showFixedFields && (
              <Section title="Fixed Charges / Contract" icon={Zap} color="cyan">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Subscription Reference" name="subscription_reference" value={form.subscription_reference} onChange={handleChange} />
                  <Field label="Monthly Fixed Amount ($)" name="fixed_monthly_amount" value={form.fixed_monthly_amount} onChange={handleChange} type="number" />
                  <Field label="Contract Start Date"    name="contract_start_date"   value={form.contract_start_date}   onChange={handleChange} type="date" />
                  <Field label="Renewal / Expiry Date"  name="contract_renewal_date" value={form.contract_renewal_date} onChange={handleChange} type="date" />
                  <SelectField label="Billing Frequency" name="billing_frequency" value={form.billing_frequency} onChange={handleChange}>
                    <option value="">— Select —</option>
                    {BILLING_FREQ.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                  </SelectField>
                </div>
              </Section>
            )}

            {/* ── Notes ── */}
            <Section title="Notes" icon={FileText} color="gray" defaultOpen={false}>
              <textarea
                name="notes"
                value={form.notes || ''}
                onChange={handleChange}
                rows={4}
                placeholder="Internal notes about this supplier..."
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 resize-none"
              />
            </Section>

          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-700 flex-shrink-0">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-white text-sm font-medium transition-colors"
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Supplier'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal ajout catégorie */}
      {catModalOpen && (
        <SupplierCategoryModal
          groups={groups}
          onClose={() => setCatModalOpen(false)}
          onCreated={handleCategoryAdded}
        />
      )}
    </>
  );
};

export default SupplierModal;